import React, { useCallback, useEffect, useState } from "react";
import apiFetch from "../../../context/api";
import { TableHead, Th, Tr, Td } from "../../Elements/DataTable";

function fmtG(n) {
  return `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} g`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

/**
 * Informe de merma de dispensación (MERMA_PLAN.md v1).
 * Merma = peso real báscula − gramos cobrados, por línea de venta por peso.
 */
export default function MermaReport({ token, venueID }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState({ entries: [], total_merma_g: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token || !venueID) return;
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const qs = params.toString();
      const res = await apiFetch(`/v1/venues/${venueID}/merma-report${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Error al cargar el informe de merma");
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, venueID, from, to]);

  useEffect(() => { load(); }, [load]);

  const entries = data?.entries || [];

  return (
    <div className="space-y-4">
      {/* Filtros + total */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 px-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 px-2 text-sm" />
          </div>
          {(from || to) && (
            <button onClick={() => { setFrom(""); setTo(""); }}
              className="h-9 rounded-lg border border-gray-300 px-3 text-sm text-gray-600 hover:bg-gray-50">
              Limpiar
            </button>
          )}
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2">
          <div className="text-xs font-medium text-amber-700">Merma total</div>
          <div className="text-lg font-bold text-amber-800">{fmtG(data?.total_merma_g)}</div>
        </div>
      </div>

      {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm">{error}</div>}

      <div className="overflow-x-auto rounded-md border border-[var(--border)] bg-[var(--card)]">
        <table className="min-w-full text-sm">
          <TableHead>
            <Th>Fecha</Th>
            <Th>Producto</Th>
            <Th>Socio</Th>
            <Th align="right">Cobrado</Th>
            <Th align="right">Real báscula</Th>
            <Th align="right">Merma</Th>
          </TableHead>
          <tbody>
            {loading ? (
              <tr><Td align="center" colSpan={6} className="px-4 py-6 text-[var(--muted-foreground)]">Cargando…</Td></tr>
            ) : entries.length === 0 ? (
              <tr><Td align="center" colSpan={6} className="px-4 py-6 text-[var(--muted-foreground)]">Sin merma registrada en el período.</Td></tr>
            ) : entries.map((e, i) => (
              <Tr key={i}>
                <Td>{fmtDate(e.timestamp)}</Td>
                <Td className="font-medium text-[var(--foreground)]">{e.product_name || "—"}</Td>
                <Td>{e.socio || "—"}</Td>
                <Td align="right">{fmtG(e.grams_charged)}</Td>
                <Td align="right">{fmtG(e.grams_real)}</Td>
                <Td align="right" className={`font-semibold ${Number(e.merma_g) > 0 ? "text-[var(--warning)]" : "text-[var(--muted-foreground)]"}`}>
                  {fmtG(e.merma_g)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
